import { CommonModule, DatePipe } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { DataService as CoreDataService } from "@barthauer/abc/core/data";
import {
  AbcAccordionComponent,
  AbcPopupComponent,
  _AbcButtonComponent,
  _AbcContextMenuComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
  _AbcTreeComponent,
} from "@barthauer/abc/ui-components";
import { Measure, MeasureTask, Person } from "@barthauer/custom/xmt-models";
import {
  DxAccordionModule,
  DxButtonModule,
  DxDataGridModule,
  DxDateBoxModule,
  DxFormModule,
  DxNumberBoxModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTreeListModule,
} from "devextreme-angular";
import { Column } from "devextreme/ui/data_grid";
import { finalize, Subject, Subscription, takeUntil, tap } from "rxjs";
import {
  NotificationMessage,
  ToastType,
} from "../../../../models/enumeration-list.model";
import { AppService } from "../../../../services/app.service";
import { DataService, ObjectKeys } from "../../../../services/data.service";
import { NotificationService } from "../../../../services/notification.service";
import { originOfCall } from "../../../../shared/milestone-task-popup/milestone-task-popup.service";
import { MilestoneComponent } from "../../../../shared/milestone-task-popup/milestone/milestone.component";
import { TaskComponent } from "../../../../shared/milestone-task-popup/task/task.component";
import { SharedModule } from "../../../../shared/shared.module";
import { MeasureService } from "../../measure.service";
import { IDescription } from "../../measure.model";
import { ScreenService } from "../../../../services/screen.service";

@Component({
  selector: "ubt-geods-implementation-plan",
  templateUrl: "./implementation-plan.component.html",
  styleUrls: ["./implementation-plan.component.scss"],
  imports: [
    CommonModule,
    AbcPopupComponent,
    _AbcGridComponent,
    DxTreeListModule,
    AbcAccordionComponent,
    DxFormModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxNumberBoxModule,
    DxButtonModule,
    SharedModule,
    _AbcButtonComponent,
    DxPopupModule,
    DxAccordionModule,
    DxDataGridModule,
    MilestoneComponent,
    DatePipe,
    TaskComponent,
    _AbcContextMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImplementationPlanComponent implements OnInit, OnDestroy {
  @ViewChild("collaboratorsTotalTimeGrid")
  collaboratorsTotalTimeGrid: _AbcGridComponent;
  @ViewChild("contextMenu") contextMenu: _AbcContextMenuComponent;
  @ViewChild("meausreTaskTree") meausreTaskTree: _AbcGridComponent;
  @ViewChild("confirmTaskPopup") confirmTaskPopup: AbcPopupComponent;
  @ViewChild("milestoneComponent") milestoneComponent: MilestoneComponent;
  @ViewChild("taskComponent") taskComponent: TaskComponent;
  @Input() currentMeasure: Measure;
  @Input() canManageMilestones: boolean;
  @Input() canManageTasks: boolean;
  private responsiblesWithImages: Person[] = [];

  public allowedTaskParent: MeasureTask[] = [];
  public measureTask: MeasureTask = new MeasureTask(null);
  public task: MeasureTask = new MeasureTask(null);
  private measureTaskId: string;
  private taskColumns: Column[] = [];

  private subscriptions: Subscription[] = [];
  private selectedRowData: MeasureTask;
  public contextMenuItems: any[] = [
    {
      text: "",
      onClick: () => {
        this.openEditMeasureTask();
      },
    },
    {
      text: "",
      onClick: () => {
        this.openConfirmTaskDialog();
      },
    },
  ];
  private destroy$ = new Subject<void>();
  constructor(
    public dataService: DataService,
    public measureService: MeasureService,
    private appService: AppService,
    private coreDataService: CoreDataService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private readonly screenService: ScreenService,
  ) {}
  public ngOnInit(): void {
    const measures = this.appService.getMeasureTask();

    this.coreDataService
      .executeReadObjectsQuery(
        this.dataService.prepareStaffMembersWithDocuments(),
      )
      .pipe(
        takeUntil(this.destroy$),

        tap((staffMembers: ArrayBuffer) => {
          if (Array.isArray(staffMembers) && staffMembers.length != 0)
            this.responsiblesWithImages = staffMembers;
          this.initTaskColumns();
          this.initScreenListener();
        }),
      )
      .subscribe(() => {
        this.handleMeasurePopup(measures);
        this.cdr.markForCheck();
      });
  }

  private initTaskColumns(): void {
    this.taskColumns = [
      {
        caption: this.dataService.res("Ubt-MeasureTask-Label"),
        dataField: "Label",
        hidingPriority: 5,
        dataType: "string",
        sortOrder: "asc",
      },
      {
        caption: this.dataService.res("Ubt-MeasureDetail-Responsible"),
        dataField: "Responsible",
        dataType: "string",
        hidingPriority: 3,
        lookup: {
          dataSource: this.responsiblesWithImages,
          displayExpr: this.dataService.getResponsibleFullName,
          valueExpr: "Id",
        },
      },
      {
        caption: this.dataService.res("Ubt-MeasureTask-Target-date"),
        dataField: "EndDate",
        dataType: "date",
        hidingPriority: 2,
        alignment: "center",
        sortOrder: "desc",
      },
      {
        caption: this.dataService.res("Ubt-MeasureDetail-Type"),
        dataField: "Type",
        dataType: "number",
        alignment: "center",
        hidingPriority: 1,
        lookup: this.getLookupColumn(),
      },
      {
        caption: this.dataService.res("Ubt-MeasureTask-DegreeOfImplementation"),
        dataField: "DegreeOfImplementation",
        hidingPriority: 4,
        alignment: "center",
      },
      {
        type: "buttons",
        caption: "",
        minWidth: 50,
        alignment: "center",
      },
    ];
  }

  initScreenListener() {
    this.screenService.screen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ screenSize, isMenuOpened }) => {
        if (!this.taskColumns) return;

        this.screenService.setGridColumnsWidth(
          this.taskColumns,
          this.meausreTaskTree,
          screenSize,
          isMenuOpened,
          this.getColumnWidth,
        );
      });
  }

  private handleMeasurePopup(measures: any): void {
    if (!measures?.Id) return;

    this.measureService.isCreatingSubTask = false;

    if (measures.Type === 0) {
      this.milestoneComponent.showPopup(
        this.currentMeasure,
        measures,
        true,
        originOfCall.Measure,
      );
    } else {
      this.taskComponent.showPopup(
        this.currentMeasure,
        measures,
        true,
        originOfCall.Measure,
      );
    }

    this.appService.setMeasureTask(new MeasureTask(null));
  }
  get locale(): string {
    return this.appService.locale;
  }

  private getColumnWidth = (
    key: string,
    isMenuOpened: boolean,
    largeScreenAddition: number = 0,
  ): string => {
    switch (key) {
      case "Label":
        const mobileWidth = this.getMobileWidth();

        if (mobileWidth) {
          return `${mobileWidth}px`;
        }

        return isMenuOpened
          ? `${(150 + largeScreenAddition).toString()}px`
          : `${(200 + largeScreenAddition).toString()}px`;
      case "Responsible":
        return isMenuOpened
          ? `${(100 + largeScreenAddition).toString()}px`
          : `${(150 + largeScreenAddition).toString()}px`;
      case "Type":
        return isMenuOpened
          ? `${(100 + largeScreenAddition).toString()}px`
          : `${(150 + largeScreenAddition).toString()}px`;

      case "EndDate":
        return isMenuOpened
          ? `${(150 + largeScreenAddition).toString()}px`
          : `${(200 + largeScreenAddition).toString()}px`;
      case "DegreeOfImplementation":
        return isMenuOpened
          ? `${(80 + largeScreenAddition).toString()}px`
          : `${(100 + largeScreenAddition).toString()}px`;
      default:
        return "30px";
    }
  };

  private getMobileWidth(): number {
    if (window.innerWidth < 400) return 135;
    if (window.innerWidth < 500) return 120;
    if (window.innerWidth < 335) return 80;
  }
  private getLookupColumn(): {
    valueExpr: string;
    displayExpr: string;
    dataSource: IDescription[];
  } {
    const description: IDescription[] = [
      {
        value: 0,
        name: this.dataService.res("Ubt-MeasureDetail-Milestone"),
      },
      { value: 1, name: this.dataService.res("Ubt-MeasureDetail-Task") },
    ];
    return { valueExpr: "value", displayExpr: "name", dataSource: description };
  }

  public onCellClick(e) {
    if (e.rowType === "data") {
      if (e.column.type === "buttons") {
        e.cellElement.className = "context-menu-icon" + e.rowIndex;
        e.cellElement.innerHTML = this.contextMenu.visible
          ? DefaultIcons.CLOSE
          : DefaultIcons.CONTEXTMENU;
        this.contextMenu.target = ".context-menu-icon" + e.rowIndex;
        const isVisible =
          (e.data.Type === 0 && !this.canManageMilestones) ||
          (e.data.Type === 1 && !this.canManageTasks);

        if (!isVisible) {
          this.contextMenu.instance.show();
        }

        this.selectedRowData = e.row?.data ?? null;
        if (e.data.Type === 1) {
          this.contextMenuItems[0].text = this.dataService.res(
            "Ubt-MeasureTask-Update-Task",
          );
          this.contextMenuItems[1].text = this.dataService.res(
            "Ubt-MeasureTask-Delete-Task",
          );
        } else {
          this.contextMenuItems[0].text = this.dataService.res(
            "Ubt-MeasureTask-Update-Milestone",
          );
          this.contextMenuItems[1].text = this.dataService.res(
            "Ubt-MeasureTask-Delete-Milestone",
          );
        }
      }
    }
  }

  public cellPrepared(e): void {
    if (e.column?.command === "adaptive") {
      e.event?.stopPropagation();
      return;
    }
    if (e.rowType === "data") {
      if (e.column.type === "buttons") {
        e.cellElement.style.cursor = "pointer";
        e.cellElement.innerHTML = DefaultIcons.CONTEXTMENU;
        this.contextMenu.visible = false;
      }

      if (e.column.dataField === "Type") {
        const type = e.row.data.Type;
        let cellElement = e.cellElement.innerHTML;
        const element = e.cellElement;
        const newDiv = document.createElement("div");
        newDiv.style.display = "flex";
        const paragraph = document.createElement("p");
        newDiv.append(paragraph);
        element.textContent = "";
        element.append(newDiv);

        switch (type) {
          case 0:
            cellElement = this.dataService.res("Ubt-MeasureDetail-Milestone");
            paragraph.append(cellElement);
            this.addStyleToStatusDatafield(paragraph, "#fdcb8e");
            break;
          case 1:
            cellElement = this.dataService.res("Ubt-MeasureDetail-Task");
            paragraph.append(cellElement);
            this.addStyleToStatusDatafield(paragraph, "#8f98cd");
            break;
        }
      } else if (e.column.dataField === "DegreeOfImplementation") {
        if (e.data.Type === 1) {
          e.cellElement.innerText = e.data.DegreeOfImplementation
            ? e.data.DegreeOfImplementation + " %"
            : "0 %";
        } else if (e.data.Type === 0) {
          e.cellElement.innerText = " ";
        }
      }
    }
  }

  private addStyleToStatusDatafield(
    element: HTMLElement,
    backColor: string,
  ): void {
    element.style.color = "#000000";
    element.style.backgroundColor = backColor;
    element.style.opacity = "0.76";
    element.style.borderRadius = "7px";
    element.style.textAlign = "left";
    element.style.fontWeight = "550";
    element.style.fontFamily = "Poppins";
    element.style.padding = "4px";
    element.style.textOverflow = "ellipsis";
    element.style.whiteSpace = "nowrap";
    element.style.overflow = " hidden";
    element.style.margin = "0";
  }

  public rowPrepared(e) {
    if (e.rowType === "data") {
      e.rowElement.style.position = "relative";
    }
  }

  public deleteTask(event): void {
    this.subscriptions.push(
      this.dataService
        .deleteObject(ObjectKeys.TASK, this.measureTaskId)
        .subscribe(
          () => {
            this.notificationService.callNotification({
              message: NotificationMessage.ValidDeleteMessage,
              type: ToastType.SUCCESS,
            });
            const index = this.currentMeasure.MeasureTasks.findIndex(
              (item) => item.Id === this.measureTaskId,
            );
            this.currentMeasure.MeasureTasks.splice(index, 1);
            this.confirmTaskPopup.instance.hide();
            this.cdr.markForCheck();
          },
          (error) => {
            console.error(error);
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
          },
        ),
    );
  }

  public addMilestone = (): void => {
    this.milestoneComponent.showPopup(
      this.currentMeasure,
      new MeasureTask(null),
      false,
      originOfCall.Measure,
    );
    this.cdr.markForCheck();
  };

  public addTask = (e): void => {
    this.measureService.isCreatingSubTask = true;
    this.taskComponent.showPopup(
      this.currentMeasure,
      new MeasureTask(null),
      false,
      originOfCall.Measure,
    );
    this.cdr.markForCheck();
  };

  public openEditMeasureTask(): void {
    this.measureTask = new MeasureTask(this.selectedRowData);
    this.cdr.markForCheck();
    if (this.selectedRowData.Type === 1) {
      this.measureService.isCreatingSubTask = false;
      this.taskComponent.showPopup(
        this.currentMeasure,
        this.measureTask,
        true,
        originOfCall.Measure,
      );
    } else {
      this.milestoneComponent.showPopup(
        this.currentMeasure,
        this.measureTask,
        true,
        originOfCall.Measure,
      );
    }
    this.cdr.markForCheck();
  }

  public openConfirmTaskDialog(): void {
    this.measureTaskId = this.selectedRowData.Id;
    this.confirmTaskPopup.instance.show();
  }

  public abortTaskDelete() {
    this.confirmTaskPopup.instance.hide();
  }

  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => subscription.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }
}

export enum DefaultIcons {
  CLOSE = `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15" height="15" viewBox="0,0,256,256">
  <g fill="#6e6e7d" fill-rule="nonzero" stroke="none" stroke-width="1" stroke-linecap="butt" stroke-linejoin="miter" stroke-miterlimit="10" stroke-dasharray="" stroke-dashoffset="0" font-family="none" font-weight="none" font-size="none" text-anchor="none" style="mix-blend-mode: normal"><g transform="scale(5.12,5.12)"><path d="M9.15625,6.3125l-2.84375,2.84375l15.84375,15.84375l-15.9375,15.96875l2.8125,2.8125l15.96875,-15.9375l15.9375,15.9375l2.84375,-2.84375l-15.9375,-15.9375l15.84375,-15.84375l-2.84375,-2.84375l-15.84375,15.84375z"></path></g></g>
  </svg>`,
  CONTEXTMENU = `<svg id="context-menu-icon" data-name="Ebene 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><defs><style>.context-menu-icon{fill:#6e6e7d;}</style></defs><circle class="context-menu-icon" cx="200" cy="60" r="40"/><circle class="context-menu-icon" cx="200" cy="340" r="40"/><circle class="context-menu-icon" cx="200" cy="200" r="40"/></svg>`,
  ASSIGNED = `<svg id="cls-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><defs><style>.cls-1{fill:#afafaf;}</style></defs><path class="cls-1" d="m22.95,12.54c.64-.71,1.03-1.64,1.03-2.66,0-2.2-1.79-3.99-3.99-3.99s-3.99,1.79-3.99,3.99c0,1.02.39,1.96,1.02,2.66-2.29.37-3.96,1.48-4.23,4.74-.04.41.27.78.68.81.41.04.78-.27.81-.68.26-3.03,1.65-3.54,5.7-3.54s5.44.51,5.7,3.54c.03.39.36.69.75.69.02,0,.04,0,.06,0,.41-.04.72-.4.68-.81-.28-3.26-1.95-4.37-4.24-4.74Zm-2.96-5.15c1.37,0,2.49,1.11,2.49,2.49s-1.12,2.49-2.49,2.49-2.49-1.12-2.49-2.49,1.12-2.49,2.49-2.49Z"/><path class="cls-1" d="m34.25,23.26c-.84,0-1.61.26-2.26.7l-2.97-2.95c1.7-2.04,2.72-4.66,2.72-7.51,0-6.48-5.27-11.75-11.75-11.75s-11.75,5.27-11.75,11.75c0,2.85,1.03,5.47,2.73,7.51l-2.97,2.95c-.64-.44-1.42-.7-2.26-.7-2.21,0-4,1.79-4,4s1.79,4,4,4,4-1.79,4-4c0-.83-.25-1.6-.68-2.24l2.95-2.93c1.21,1.12,2.64,1.99,4.23,2.53l-1.46,5.65c-.09,0-.17-.03-.25-.03-2.21,0-4,1.79-4,4s1.79,4,4,4,4-1.79,4-4c0-1.59-.94-2.96-2.29-3.6l1.45-5.63c.75.15,1.52.23,2.3.23s1.56-.08,2.3-.23l1.46,5.63c-1.35.64-2.29,2.01-2.29,3.6,0,2.21,1.79,4,4,4s4-1.79,4-4-1.79-4-4-4c-.09,0-.17.02-.25.03l-1.46-5.65c1.59-.54,3.03-1.41,4.23-2.53l2.95,2.93c-.43.64-.68,1.41-.68,2.24,0,2.21,1.79,4,4,4s4-1.79,4-4-1.79-4-4-4Zm-17.22,10.99c0,1.38-1.12,2.5-2.5,2.5s-2.5-1.12-2.5-2.5,1.12-2.5,2.5-2.5,2.5,1.12,2.5,2.5Zm10.93,0c0,1.38-1.12,2.5-2.5,2.5s-2.5-1.12-2.5-2.5,1.12-2.5,2.5-2.5,2.5,1.12,2.5,2.5Zm-22.22-4.49c-1.38,0-2.5-1.12-2.5-2.5s1.12-2.5,2.5-2.5,2.5,1.12,2.5,2.5-1.12,2.5-2.5,2.5Zm14.25-6.01c-5.65,0-10.25-4.6-10.25-10.25S14.35,3.25,20,3.25s10.25,4.6,10.25,10.25-4.6,10.25-10.25,10.25Zm14.25,6.01c-1.38,0-2.5-1.12-2.5-2.5s1.12-2.5,2.5-2.5,2.5,1.12,2.5,2.5-1.12,2.5-2.5,2.5Z"/></svg>`,
  MEASURETASK = '<svg id="cls-2" xmlns="http://www.w3.org/2000/svg" style="width:25px" viewBox="0 0 40 40"><defs><style>.cls-2 {fill: var(--primary-color);}</style></defs><path class="cls-1" d="m15.57,11.05l-3.68,3.68-1.36-1.36c-.29-.29-.77-.29-1.06,0s-.29.77,0,1.06l1.89,1.89c.15.15.34.22.53.22s.38-.07.53-.22l4.21-4.21c.29-.29.29-.77,0-1.06s-.77-.29-1.06,0Z"/><path class="cls-1" d="m26.7,19.55h-7.31c-.41,0-.75.34-.75.75s.34.75.75.75h7.31c.41,0,.75-.34.75-.75s-.34-.75-.75-.75Z"/><path class="cls-1" d="m19.39,24.27h4.67c.41,0,.75-.34.75-.75s-.34-.75-.75-.75h-4.67c-.41,0-.75.34-.75.75s.34.75.75.75Z"/><path class="cls-1" d="m15.57,19.05l-3.68,3.68-1.36-1.36c-.29-.29-.77-.29-1.06,0s-.29.77,0,1.06l1.89,1.89c.15.15.34.22.53.22s.38-.07.53-.22l4.21-4.21c.29-.29.29-.77,0-1.06s-.77-.29-1.06,0Z"/><path class="cls-1" d="m26.7,27.55h-7.31c-.41,0-.75.34-.75.75s.34.75.75.75h7.31c.41,0,.75-.34.75-.75s-.34-.75-.75-.75Z"/><path class="cls-1" d="m24.06,30.78h-4.67c-.41,0-.75.34-.75.75s.34.75.75.75h4.67c.41,0,.75-.34.75-.75s-.34-.75-.75-.75Z"/><path class="cls-1" d="m15.57,27.05l-3.68,3.68-1.36-1.36c-.29-.29-.77-.29-1.06,0s-.29.77,0,1.06l1.89,1.89c.15.15.34.22.53.22s.38-.07.53-.22l4.21-4.21c.29-.29.29-.77,0-1.06s-.77-.29-1.06,0Z"/><path class="cls-1" d="m33.36,1.75H9.94c-1.2,0-2.17.97-2.17,2.17v1.13h-1.13c-1.2,0-2.17.97-2.17,2.17v28.87c0,1.2.97,2.17,2.17,2.17h23.42c1.2,0,2.17-.97,2.17-2.17v-1.13h1.13c1.2,0,2.17-.97,2.17-2.17V3.92c0-1.19-.97-2.17-2.17-2.17Zm-2.63,34.33c0,.37-.3.67-.67.67H6.64c-.37,0-.67-.3-.67-.67V7.22c0-.37.3-.67.67-.67h13.7c.37,0,.67.3.67.67v4.33h-1.61c-.41,0-.75.34-.75.75s.34.75.75.75h1.61v.36c0,.5.14.96.36,1.37h-1.98c-.41,0-.75.34-.75.75s.34.75.75.75h4.47s0,0,0,0h6.19c.37,0,.67.3.67.67v19.14Zm-1.89-21.31h-4.97c-.76,0-1.37-.62-1.37-1.37v-4.96l6.34,6.34Zm5.19,18.01c0,.37-.3.67-.67.67h-1.13v-16.51c0-.62-.26-1.18-.68-1.57h0s-9.64-9.64-9.64-9.64h0c-.4-.41-.95-.68-1.57-.68h-11.06v-1.13c0-.37.3-.67.67-.67h23.42c.37,0,.67.3.67.67v28.87Z"/></svg>',
  MILESTONE = '<svg id="cls-2" xmlns="http://www.w3.org/2000/svg" style="width:25px"  viewBox="0 0 40 40"><defs><style>.cls-2{fill:var(--primary-color);}</style></defs><path class="cls-1" d="m23.98,13.05l-5.48,5.48-2.48-2.48c-.29-.29-.77-.29-1.06,0s-.29.77,0,1.06l3.01,3.01c.15.15.34.22.53.22s.38-.07.53-.22l6.01-6.01c.29-.29.29-.77,0-1.06s-.77-.29-1.06,0Z"/><path class="cls-1" d="m23.98,23.65l-5.48,5.48-2.48-2.48c-.29-.29-.77-.29-1.06,0s-.29.77,0,1.06l3.01,3.01c.15.15.34.22.53.22s.38-.07.53-.22l6.01-6.01c.29-.29.29-.77,0-1.06s-.77-.29-1.06,0Z"/><path class="cls-1" d="m31.08,5.04h-4.23c-.3-1.03-1.24-1.8-2.37-1.8h-.51c-.58,0-1.15-.23-1.72-.7-1.27-1.04-3.23-1.04-4.5,0-.39.32-1,.7-1.72.7h-.51c-1.13,0-2.07.76-2.37,1.8h-4.23c-1.2,0-2.17.97-2.17,2.17v28.87c0,1.2.97,2.17,2.17,2.17h22.16c1.2,0,2.17-.97,2.17-2.17V7.2c0-1.2-.97-2.17-2.17-2.17Zm-16.54.68c0-.54.44-.98.98-.98h.51c.92,0,1.84-.36,2.67-1.04.74-.61,1.86-.61,2.6,0,.83.68,1.75,1.04,2.67,1.04h.51c.54,0,.98.44.98.98v1.66c0,.21-.17.38-.38.38h-10.14c-.21,0-.38-.17-.38-.38v-1.66Zm17.2,30.35c0,.37-.3.67-.67.67H8.92c-.37,0-.67-.3-.67-.67V7.2c0-.37.3-.67.67-.67h4.13v.84c0,1.04.85,1.88,1.88,1.88h10.14c1.04,0,1.88-.85,1.88-1.88v-.84h4.13c.37,0,.67.3.67.67v28.87Z"/></svg>',
}
